import type { BracketExportOptions } from './export-options'

const EXPORT_TIMEOUT_MS = 30_000
const RESOURCE_TIMEOUT_MS = 10_000

type ExportRenderer = (
  node: HTMLElement,
  options: BracketExportOptions & { fetchRequestInit: RequestInit },
) => Promise<HTMLCanvasElement>

type ExportDependencies = {
  loadRenderer?: () => Promise<{ toCanvas: ExportRenderer }>
  timeoutMs?: number
  resourceTimeoutMs?: number
}

function createTimeoutError(): Error {
  const error = new Error('Bracket image export timed out')
  error.name = 'TimeoutError'
  return error
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        reject(new Error('Bracket export produced an empty image'))
        return
      }
      resolve(blob)
    }, 'image/png')
  })
}

export async function createBracketExportImage(
  node: HTMLElement,
  options: BracketExportOptions,
  drawWatermark: (canvas: HTMLCanvasElement) => void,
  {
    loadRenderer = () => import('html-to-image'),
    timeoutMs = EXPORT_TIMEOUT_MS,
    resourceTimeoutMs = RESOURCE_TIMEOUT_MS,
  }: ExportDependencies = {},
): Promise<Blob> {
  const controller = new AbortController()
  let active = true
  let canvas: HTMLCanvasElement | undefined
  let resourceTimeout: ReturnType<typeof setTimeout> | undefined
  let exportTimeout: ReturnType<typeof setTimeout> | undefined

  const releaseCanvas = () => {
    if (!canvas) return
    canvas.width = 0
    canvas.height = 0
    canvas = undefined
  }
  const ensureActive = () => {
    if (!active || controller.signal.aborted) {
      throw createTimeoutError()
    }
  }

  const timeout = new Promise<never>((_, reject) => {
    exportTimeout = setTimeout(() => {
      active = false
      controller.abort()
      reject(createTimeoutError())
    }, timeoutMs)
  })

  const renderImage = async () => {
    const { toCanvas } = await loadRenderer()
    ensureActive()
    // 所有资源请求共享取消信号，防止慢网络让导出一直等待。
    resourceTimeout = setTimeout(() => controller.abort(), resourceTimeoutMs)
    try {
      canvas = await toCanvas(node, {
        ...options,
        fetchRequestInit: { signal: controller.signal },
      })
      clearTimeout(resourceTimeout)
      ensureActive()
      drawWatermark(canvas)
      ensureActive()
      const blob = await canvasToPngBlob(canvas)
      ensureActive()
      return blob
    } finally {
      // 超时后才返回的画布也要释放，且不能继续生成或下载图片。
      releaseCanvas()
    }
  }

  try {
    return await Promise.race([renderImage(), timeout])
  } catch (error) {
    if (controller.signal.aborted) throw createTimeoutError()
    throw error
  } finally {
    active = false
    clearTimeout(exportTimeout)
    clearTimeout(resourceTimeout)
    controller.abort()
  }
}

export function triggerBracketDownload(url: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = 'snapbracket.png'
  document.body.appendChild(link)
  try {
    link.click()
  } finally {
    link.remove()
  }
}
